/**
 * The SYSTEM ADMIN configuration area's public surface (project brief
 * §18/§30). `router.tsx` and `nav.ts` are READ ONLY for this pass (see the
 * task's FILE OWNERSHIP) — this module exports `adminRoutes`/`adminNavItems`
 * in the exact shapes those two files already use, so wiring them in is a
 * one-line splice rather than a rewrite. See this pass's HANDOFF for the
 * exact lines.
 *
 * Plain `.ts`, not `.tsx` (`adminRoutes`'s element needs a value, not JSX
 * syntax, to stay valid outside a `.tsx` file) — `createElement` is used
 * directly rather than a `<Screen />` tag for that reason.
 */
import { createElement, lazy, type ComponentType } from 'react';
import type { RouteObject } from 'react-router-dom';
import { PERMISSIONS } from '@ithelpdesk/shared';
import type { NavItem } from '../../app/nav';
import { ADMIN_SECTIONS } from './sections';

export { ADMIN_SECTIONS } from './sections';
import { AdminLayout } from './AdminLayout';

const AdminUsersScreen = lazy(() => import('./AdminUsersScreen'));
const AdminAuditLogScreen = lazy(() => import('./AdminAuditLogScreen'));
const AdminRolesScreen = lazy(() => import('./AdminRolesScreen'));
const AdminTeamsScreen = lazy(() => import('./AdminTeamsScreen'));
const AdminDirectoryScreen = lazy(() => import('./AdminDirectoryScreen'));
const AdminCatalogueScreen = lazy(() => import('./AdminCatalogueScreen'));
const AdminSlaScreen = lazy(() => import('./AdminSlaScreen'));
const AdminRoutingScreen = lazy(() => import('./AdminRoutingScreen'));
const AdminSettingsScreen = lazy(() => import('./AdminSettingsScreen'));

/**
 * `AdminUsersScreen`/`AdminAuditLogScreen` predate the section-tab
 * `AdminLayout` (project brief §18/§30's full admin area) and render their
 * own heading + padding, so rather than restyle those two screens (and risk
 * their existing tests), the layout's tab strip is spliced in at the route
 * level here — the newer screens render `<AdminLayout>` themselves.
 */
function withLegacyLayout(section: string, Screen: ComponentType) {
  return createElement(AdminLayout, { section, children: createElement(Screen) });
}

/**
 * Spread into the `<RequireAuth><AppShell /></RequireAuth>` route's children
 * in `router.tsx`, alongside the existing `<Route path="..." element={...} />`
 * entries — see HANDOFF for the exact JSX line, since a plain data array
 * cannot itself carry `<Route>` elements.
 */
export const adminRoutes: RouteObject[] = [
  { path: 'admin/users', element: withLegacyLayout('users', AdminUsersScreen) },
  { path: 'admin/roles', element: createElement(AdminRolesScreen) },
  { path: 'admin/teams', element: createElement(AdminTeamsScreen) },
  { path: 'admin/directory', element: createElement(AdminDirectoryScreen) },
  { path: 'admin/catalogue', element: createElement(AdminCatalogueScreen) },
  { path: 'admin/sla', element: createElement(AdminSlaScreen) },
  { path: 'admin/routing', element: createElement(AdminRoutingScreen) },
  { path: 'admin/settings', element: createElement(AdminSettingsScreen) },
  { path: 'admin/audit-log', element: withLegacyLayout('audit-log', AdminAuditLogScreen) },
];

/**
 * Appended to `NAV_ITEMS` in `nav.ts` — gated on `ADMIN_READ`, the one
 * permission every admin screen requires to even open (per-action buttons
 * inside each screen additionally check the specific `ADMIN_*_WRITE`
 * permission, same pattern as the rest of the app). UX only: every
 * `/admin/*` endpoint re-checks on the server.
 */
export const adminNavItems: NavItem[] = ADMIN_SECTIONS.map((s) => ({
  label: s.label,
  to: s.path,
  icon: s.icon,
  anyOfPermissions: [PERMISSIONS.ADMIN_READ],
  group: 'admin' as const,
}));
